import { Link } from "react-router-dom";
import { Panel } from "@/components/system";
import { useAudience } from "@/contexts/AudienceContext";
import { conditionsForSpecialty } from "@/lib/conditions";
import { conditionPath } from "@/lib/local-seo";
import { PATIENT_GUIDE } from "@/lib/patient-guide";
import { specialtyContent } from "@/lib/specialty-content";
import { withArticle } from "@/lib/structured-data";

/**
 * Specialty-page explainer. Patients (default) get "what does this kind of
 * specialist do" plus how referrals work; GP mode gets referral prompts.
 * The patient version is also what the prerender ships to crawlers.
 */
export function SpecialtyAudiencePanel({ slug, name }: { slug: string; name: string }) {
  const { isGpMode } = useAudience();
  const content = specialtyContent(slug);
  const practitioner = content?.practitioner ?? `${name.toLowerCase()} specialist`;
  const conditions = conditionsForSpecialty(slug);

  if (isGpMode) {
    if (!content) return null;
    return (
      <Panel aria-labelledby="specialty-gp-notes">
        <div className="ds-stack" style={{ gap: 8 }}>
          <p className="ds-eyebrow ds-eyebrow--teal">For GPs</p>
          <h2 id="specialty-gp-notes" className="ds-title">Referring to {withArticle(practitioner)}</h2>
          <p className="ds-small">Useful to include in the referral:</p>
        </div>
        <ul className="ds-list">
          {content.gpNotes.map((note) => <li key={note}>{note}</li>)}
        </ul>
        <p className="ds-fine">General prompts only. Each profile lists the practice's own referral requirements where published.</p>
      </Panel>
    );
  }

  return (
    <Panel aria-labelledby="specialty-patient-intro">
      <div className="ds-explainer__grid">
      <div className="ds-explainer__main">
        <h2 id="specialty-patient-intro" className="ds-title">What does {withArticle(practitioner)} do?</h2>
        {content ? (
          <>
            <p className="ds-body">{content.patientIntro}</p>
            <p className="ds-small"><strong>Common reasons people are referred</strong></p>
            <ul className="ds-list">
              {content.commonReasons.map((reason) => <li key={reason}>{reason}</li>)}
            </ul>
          </>
        ) : null}
      </div>
      <div className="ds-explainer__side">
        <div className="ds-stack" style={{ gap: 6 }}>
          <p className="ds-small"><strong>Do I need a referral?</strong></p>
          <p className="ds-small">
            To claim a Medicare rebate you need a referral, usually from your GP.{" "}
            <Link className="ds-link" to={PATIENT_GUIDE.path}>How referrals, Medicare and costs work</Link>
          </p>
        </div>
        {conditions.length > 0 ? (
          <div className="ds-stack" style={{ gap: 6 }}>
            <p className="ds-small"><strong>Condition guides</strong></p>
            <ul className="ds-stack" style={{ gap: 4, listStyle: "none", margin: 0, padding: 0 }}>
              {conditions.map((condition) => (
                <li key={condition.slug}>
                  <Link className="ds-link ds-small" to={conditionPath(condition.slug)}>
                    {condition.heading.replace(/ in Canberra$/, "")}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        ) : null}
        <p className="ds-fine">General information only, not medical advice.</p>
      </div>
      </div>
    </Panel>
  );
}
